// src/Volume.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout'; 
import { useNavigate } from 'react-router-dom';
import './styles.css'; 
import { useAuth } from './StoreValue';
import { markStepComplete } from './StepGuard';
import { getAgentConfig, isPromotix, isIntelecom } from '../utils/agentConfig';

const Volume = () => {
    const [loading, setLoading] = useState(false);  // Add loading state
    const [selectedSections, setSelectedSections] = useState({
        credit: false,
        ach: false,
    });
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { ContactGuid,agentIdVal,leadGuid, updateAgentId, updateContactGuid, updateLeadGuid } = useAuth();
    const [creditBatch, setCreditBatch] = useState({
        monthlyVolume: '',
        dailyVolume: '',
        monthlyCount:'',
        monthlyCountRefunds: '',
        monthlyVolumeRefunds:'',
        averageTicket: '',
        maximumTicket: '',
        percentSwiped: '',
        percentKeyed: '',
        percentInternet: '',
        percentMailOrder: '',
        settlementMethod: '',
        priorAcceptance: '0',
        priorTermination: '0',
        cardTypes: {
            Visa: true,
            MasterCard: true,
            Amex: true,
            Discover: true,
        },
    });

    const [achBatch, setAchBatch] = useState({
        achmonthlyVolumeVal: '',
        achdailyVolume: '',
        achmonthlyCount:'',
        achmonthlyCountRefunds: '',
        achmonthlyVolumeRefunds:'',
        achaverageTicket: '',
        achmaximumTicket: '',
        achpercentNachaCCD: '',
        achpercentNachaPOP: '',
        achpercentNachaPPD: '',
        achpercentNachaTEL: '',
        achpercentNachaWEB: '',
        achpercentNachaARC:'',
        achpercentNachaBOC:'',
        achsettlementMethod: '',
        achpriorAcceptance: '0',
        achpriorTermination: '0',
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [revioVolumeError, setRevioVolumeError] = useState('');
    const navigate = useNavigate();
    
    // Helper function to get session storage with expiry
    function getSessionStorageWithExpiry(key) {
        const itemStr = sessionStorage.getItem(key);
        if (!itemStr) return null; // Handle missing key
      
        const itemObj = JSON.parse(itemStr);
        if (!itemObj || !itemObj.value || !itemObj.expiry) return null; // Handle invalid object
      
        const now = new Date();
        if (now.getTime() > itemObj.expiry) {
          sessionStorage.removeItem(key); // Remove expired item
          return null;
        }
      
        return itemObj.value; // Return the value
    }
    
    // Agent classification — derived from centralized agent config
    const valueAgentId = getSessionStorageWithExpiry("agentId");
    const agentConfig = getAgentConfig(valueAgentId);
    const ediningExists   = agentConfig.business.lockFields === true;
    const mwRevioExists   = agentConfig.business.mccDefault === '8661' && agentConfig.completionPath === 'extended';
    const mwGeneralExists = agentConfig.business.mccDefault === '8661' && agentConfig.completionPath === 'standard';
    const ensoraExists    = agentConfig.business.mccFilter === 'fixed';
    const creditOnly      = agentConfig.volume.creditOnly === true;
    const promotixExists  = isPromotix(valueAgentId);
    // `revioExists` is base Rev.IO only (not MW) — matches the original REACT_APP_REVIO membership
    const revioExists     = agentConfig.volume.lockPpd === true;
    const intelecomExists = isIntelecom(valueAgentId);
    const creditEntryDefaults = agentConfig.volume.creditEntryDefaults;
    // PromoTix merchants sell tickets, so "Average Ticket" reads as the price of one ticket
    // rather than the value of an order. Display label only — the field name, state key and the
    // AverageTicket property sent to TRX are unchanged.
    const averageTicketLabel = promotixExists ? 'Average Order Size' : 'Average Ticket';
    
    const handleCardTypeChange = (e, cardType) => {
        const isChecked = e.target.checked;
      
        setCreditBatch((prevBatch) => ({
          ...prevBatch,
          cardTypes: {
            ...prevBatch.cardTypes,
            [cardType]: isChecked,
          },
        }));
      
        // If unchecked, invoke the removeAccount function
        if (!isChecked) {
          removeAccount(cardType);
        }
      };
      
      const removeAccount = async (cardType) => {
        try {
          // Prepare the request payload
          const data = {
            leadguidVal: leadGuid,
        agentId: agentIdVal,
        ContactGuidVal:ContactGuid,
            Detail: {
              TranType: "TranType", 
              TranAction: "TranAction",
              Ip: "Ip", 
            },
            Batch: {
              BatchType: "Credit",
              Accounts: {
                Account: [
                    {
                      CardType: cardType, 
                    },
                  ],
              }, 
            }, 
          }; 
          
      
          // Send the API request
          const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);

          var responseCode = response.data.response.result.responseCode;
          var errorInfo = response.data.response.result.errorInfo;
          
          if (responseCode === '00' || responseCode === 'X7') {

            setCreditBatch((prevBatch) => ({
                ...prevBatch,
                cardTypes: {
                  ...prevBatch.cardTypes,
                  [cardType]: false,
                },
              }));
          } else {
              setCreditBatch((prevBatch) => ({
                ...prevBatch,
                cardTypes: {
                  ...prevBatch.cardTypes,
                  [cardType]: true,
                },
              }));
              setErrorMessage(errorInfo);
          }
        } catch (error) {
          console.error("Error removing account:", error);
          setCreditBatch((prevBatch) => ({
            ...prevBatch,
            cardTypes: {
              ...prevBatch.cardTypes,
              [cardType]: true,
            },
          }));
          alert("An error occurred while removing the account. Please try again.");
        }
      };
      
    
    const removeBatchSection = async (batchType) => {
        try {
            setLoading(true);
            setErrorMessage('');
            setSuccessMessage('');

            // Use correct casing to match backend expectations
            const apiTypeName = batchType === 'credit' ? 'Credit' : 'Ach';

            const data = {
                leadguidVal: leadGuid,
                agentId: agentIdVal,
                ContactGuidVal: ContactGuid,
                Detail: { TranType: "TranType", TranAction: "TranAction", Ip: "Ip" },
                Batch: { BatchType: apiTypeName },
            };

            const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);
            const responseCode = response.data.response.result.responseCode;
            const errorInfo = response.data.response.result.errorInfo;

            if (responseCode === '00' || responseCode === 'X7') {
                setSelectedSections((prev) => ({ ...prev, [batchType]: false }));

                // Reset removed batch state to clear stale values
                if (batchType === 'ach') {
                    setAchBatch({
                        achmonthlyVolumeVal: '',
                        achdailyVolume: '',
                        achmonthlyCount: '',
                        achmonthlyCountRefunds: '',
                        achmonthlyVolumeRefunds: '',
                        achaverageTicket: '',
                        achmaximumTicket: '',
                        achpercentNachaCCD: '',
                        achpercentNachaPOP: '',
                        achpercentNachaPPD: '',
                        achpercentNachaTEL: '',
                        achpercentNachaWEB: '',
                        achpercentNachaARC: '',
                        achpercentNachaBOC: '',
                        achsettlementMethod: '',
                        achpriorAcceptance: '0',
                        achpriorTermination: '0',
                    });
                } else {
                    setCreditBatch({
                        monthlyVolume: '',
                        dailyVolume: '',
                        monthlyCount: '',
                        monthlyCountRefunds: '',
                        monthlyVolumeRefunds: '',
                        averageTicket: '',
                        maximumTicket: '',
                        percentSwiped: '',
                        percentKeyed: '',
                        percentInternet: '',
                        percentMailOrder: '',
                        settlementMethod: '',
                        priorAcceptance: '0',
                        priorTermination: '0',
                        cardTypes: { Visa: true, MasterCard: true, Amex: true, Discover: true },
                    });
                }

                // Re-save the remaining batch so the backend reflects the correct final state
                if (batchType === 'ach' && selectedSections.credit && creditBatch.monthlyVolume) {
                    const creditRequest = {
                        leadguidVal: leadGuid,
                        agentId: agentIdVal,
                        ContactGuidVal: ContactGuid,
                        Detail: { TranType: "TranType", TranAction: "TranAction", Ip: "Ip" },
                        Batch: {
                            batchType: "Credit",
                            priorAcceptance: creditBatch.priorAcceptance,
                            monthlyVolume: ediningExists ? "50000" : creditBatch.monthlyVolume,
                            monthlyCountRefunds: creditBatch.monthlyCountRefunds,
                            monthlyVolumeRefunds: creditBatch.monthlyVolumeRefunds,
                            dailyVolume: ediningExists ? "1000" : creditBatch.dailyVolume,
                            monthlyCount: creditBatch.monthlyCount,
                            averageTicket: ediningExists ? "250" : creditBatch.averageTicket,
                            maximumTicket: ediningExists ? "1000" : creditBatch.maximumTicket,
                            percentInternet: ediningExists ? "100" : creditBatch.percentInternet,
                            percentSwiped: ediningExists ? "0" : creditBatch.percentSwiped,
                            percentKeyed: ediningExists ? "0" : creditBatch.percentKeyed,
                            percentMailOrder: ediningExists ? "0" : creditBatch.percentMailOrder,
                            priorTermination: creditBatch.priorTermination,
                            Accounts: {
                                Account: ((revioExists || mwRevioExists || mwGeneralExists || ensoraExists || promotixExists)
                                    ? ['Visa', 'MasterCard', 'Amex', 'Discover']
                                    : Object.keys(creditBatch.cardTypes).filter(ct => creditBatch.cardTypes[ct])
                                ).map(ct => ({ CardType: ct, AuthHost: "AuthEmulator", SettleHost: "Local" })),
                            },
                        },
                    };
                    await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, creditRequest);
                } else if (batchType === 'credit' && selectedSections.ach && achBatch.achmonthlyVolumeVal) {
                    const achRequest = {
                        leadguidVal: leadGuid,
                        agentId: agentIdVal,
                        ContactGuidVal: ContactGuid,
                        Detail: { TranType: "TranType", TranAction: "TranAction", Ip: "Ip" },
                        Batch: {
                            batchType: "Ach",
                            monthlyVolume: achBatch.achmonthlyVolumeVal,
                            monthlyCount: achBatch.achmonthlyCount,
                            priorAcceptance: achBatch.achpriorAcceptance,
                            priorTermination: achBatch.achpriorTermination,
                            averageTicket: achBatch.achavgTicket,
                            monthlyCountRefunds: achBatch.achmonthlyCountRefunds,
                            monthlyVolumeRefunds: achBatch.achmonthlyVolumeRefunds,
                            maximumTicket: achBatch.achmaximumTicket,
                            dailyVolume: achBatch.achdailyVolume,
                            PercentNachaPOP: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaPOP,
                            PercentNachaCCD: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaCCD,
                            PercentNachaWEB: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaWEB,
                            PercentNachaTEL: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaTEL,
                            PercentNachaARC: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaARC,
                            PercentNachaBOC: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0' : achBatch.achpercentNachaBOC,
                            PercentNachaPPD: (revioExists && !mwRevioExists && !mwGeneralExists) ? '100' : achBatch.achpercentNachaPPD,
                        },
                    };
                    await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, achRequest);
                }

                const batchName = batchType === 'credit' ? 'Credit' : 'ACH';
                setSuccessMessage(`${batchName} batch removed successfully.`);
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                setErrorMessage(errorInfo);
            }
        } catch (error) {
            console.error("Error removing batch:", error);
            setErrorMessage("An error occurred while removing the batch. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    
    // Set SEC defaults: MW agents get 75% PPD / 20% WEB / 5% CCD; base REVIO gets 100% PPD
    useEffect(() => {
        if ((mwRevioExists || mwGeneralExists) && !achBatch.achpercentNachaPPD) {
            setAchBatch(prev => ({
                ...prev,
                achpercentNachaPPD: '75',
                achpercentNachaWEB: '20',
                achpercentNachaCCD: '5',
                achpercentNachaTEL: '0',
                achpercentNachaPOP: '0',
                achpercentNachaARC: '0',
                achpercentNachaBOC: '0',
            }));
        } else if (revioExists && !mwRevioExists && !mwGeneralExists && !achBatch.achpercentNachaPPD) {
            setAchBatch(prev => ({
                ...prev,
                achpercentNachaPPD: '100',
                achpercentNachaCCD: '0',
                achpercentNachaTEL: '0',
                achpercentNachaPOP: '0',
                achpercentNachaARC: '0',
                achpercentNachaWEB: '0',
                achpercentNachaBOC: '0',
            }));
        } else if (ensoraExists && !achBatch.achpercentNachaPPD) {
            setAchBatch(prev => ({
                ...prev,
                achpercentNachaPPD: '30',
                achpercentNachaWEB: '40',
                achpercentNachaCCD: '30',
                achpercentNachaTEL: '0',
                achpercentNachaPOP: '0',
                achpercentNachaARC: '0',
                achpercentNachaBOC: '0',
            }));
        } else if (intelecomExists && !achBatch.achpercentNachaPPD) {
            // In-Telecom — ACH NACHA defaults: PPD 80 / WEB 10 / CCD 10 (others 0)
            setAchBatch(prev => ({
                ...prev,
                achpercentNachaPPD: '80',
                achpercentNachaWEB: '10',
                achpercentNachaCCD: '10',
                achpercentNachaTEL: '0',
                achpercentNachaPOP: '0',
                achpercentNachaARC: '0',
                achpercentNachaBOC: '0',
            }));
        }
    }, [revioExists, mwRevioExists, mwGeneralExists, ensoraExists, promotixExists, intelecomExists]);

    useEffect(() => {
        if (creditEntryDefaults && !creditBatch.percentInternet && !creditBatch.percentSwiped) {
            setCreditBatch(prev => ({
                ...prev,
                percentSwiped: creditEntryDefaults.percentSwiped ?? '0',
                percentKeyed: creditEntryDefaults.percentKeyed ?? '0',
                percentInternet: creditEntryDefaults.percentInternet ?? '0',
                percentMailOrder: creditEntryDefaults.percentMailOrder ?? '0',
            }));
        }
    }, [creditEntryDefaults]);

    useEffect(() => {
        handleBlur();
        handleBlur1();
        const fetchBatchData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/Auth/LeadLookup`, {
                params: {
                    leadGuidVal: leadGuid,
                    agentIdValue: agentIdVal,
                },
                });
                const leadData = response.data;
                
                if (leadData.result.responseCode === '00') {
                    let batches = leadData.queryResponse.batches;
    
// Ensure batches is an array
if (!Array.isArray(batches)) {
    batches = [batches]; // Wrap single object in array
}

// Check if any existing batch has data
const hasExistingData = batches[0]?.batch?.some(b => b.monthlyVolume);

// Handle both batches dynamically — guard against single-object (non-array) batch response
const batchArray = Array.isArray(batches[0]?.batch)
    ? batches[0].batch
    : batches[0]?.batch ? [batches[0].batch] : [];
batchArray.forEach((batch, index) => {
    if (batch.batchType === "Credit") {
        // Handle Credit batch
        setCreditBatch({
            monthlyVolume: batch.monthlyVolume || '',
            dailyVolume: batch.dailyVolume || '',
            monthlyCount: batch.monthlyCount || '',
            monthlyCountRefunds: batch.monthlyCountRefunds || '',
            monthlyVolumeRefunds: batch.monthlyVolumeRefunds || '',
            averageTicket: batch.averageTicket || '',
            maximumTicket: batch.maximumTicket || '',
            percentSwiped: batch.percentSwiped || '',
            percentKeyed: batch.percentKeyed || '',
            percentInternet: batch.percentInternet || '',
            percentMailOrder: batch.percentMailOrder || '',
            priorAcceptance: batch.priorAcceptance || '',
            priorTermination: batch.priorTermination || '',
            cardTypes: {
                Visa: false,
                MasterCard: false,
                Amex: false,
                Discover: false,
            },
        });

        // Update selected cards based on account card types
        if (batch.accounts?.account) {
            const updatedCardTypes = { ...creditBatch.cardTypes };
            batch.accounts.account.forEach((account) => {
                if (updatedCardTypes.hasOwnProperty(account.cardType)) {
                    updatedCardTypes[account.cardType] = true;
                }
            });
            setCreditBatch((prevBatch) => ({
                ...prevBatch,
                cardTypes: updatedCardTypes,
            }));
        }

        // Open Credit section only if existing data exists
        if (batch.monthlyVolume) {
            setSelectedSections((prev) => ({
                ...prev,
                credit: true,
            }));
        }
    }

    if (batch.batchType === "Ach") {
        // Handle ACH batch — reuse outer-scope classification flags
        const isMWAgentInEffect = mwRevioExists || mwGeneralExists;
        const isRevioOnlyAgent  = revioExists;
        const isIntelecomAgent  = intelecomExists;
        setAchBatch({
            achmonthlyVolumeVal: batch.monthlyVolume || '',
            achdailyVolume: batch.dailyVolume || '',
            achmonthlyCount: batch.monthlyCount || '',
            achmonthlyCountRefunds: batch.monthlyCountRefunds || '',
            achmonthlyVolumeRefunds: batch.monthlyVolumeRefunds || '',
            achavgTicket: batch.averageTicket || '',
            achmaximumTicket: batch.maximumTicket || '',
            achpercentNachaARC: (isRevioOnlyAgent || isIntelecomAgent) ? '0' : (batch.percentNachaARC || ''),
            achpercentNachaBOC: (isRevioOnlyAgent || isIntelecomAgent) ? '0' : (batch.percentNachaBOC || ''),
            achpercentNachaPOP: (isRevioOnlyAgent || isIntelecomAgent) ? '0' : (batch.percentNachaPOP || ''),
            achpercentNachaCCD: isMWAgentInEffect ? (batch.percentNachaCCD || '5')  : isIntelecomAgent ? (batch.percentNachaCCD || '10') : isRevioOnlyAgent ? '0' : (batch.percentNachaCCD || ''),
            achpercentNachaWEB: isMWAgentInEffect ? (batch.percentNachaWEB || '20') : isIntelecomAgent ? (batch.percentNachaWEB || '10') : isRevioOnlyAgent ? '0' : (batch.percentNachaWEB || ''),
            achpercentNachaPPD: isMWAgentInEffect ? (batch.percentNachaPPD || '75') : isIntelecomAgent ? (batch.percentNachaPPD || '80') : isRevioOnlyAgent ? '100' : (batch.percentNachaPPD || ''),
            achpercentNachaTEL: (isRevioOnlyAgent || isIntelecomAgent) ? '0' : (batch.percentNachaTEL || ''),
            achpriorAcceptance: batch.priorAcceptance || '',
            achpriorTermination: batch.priorTermination || '',
        });

        // Open ACH section only if existing data exists — never for credit-only agents, who
        // have no ACH UI and no way to remove the section once it appears.
        if (batch.monthlyVolume && !creditOnly) {
            setSelectedSections((prev) => ({
                ...prev,
                ach: true,
            }));
        }
    }
});
                }
            } catch (error) {
                console.error('Error fetching batch data:', error);
            }
        };
    
        fetchBatchData();
    }, []);
   

    const creditPercentFields = ["percentSwiped", "percentKeyed", "percentInternet", "percentMailOrder"];

    const handleInputChange = (e, field) => {
        let newValue = e.target.value;
        
        // Clear REVIO volume error when user starts typing
        if (field === 'monthlyVolume' && revioExists) {
            setRevioVolumeError('');
            setErrorMessage('');
        }
    
        // Ensure the value remains a string, even if it looks like a number
        setCreditBatch((prev) => {
            let updatedBatch = { ...prev, [field]: newValue };
    
            if (!isNaN(newValue) && parseInt(newValue, 10) === 100 && creditPercentFields.includes(field)) {
                updatedBatch = {
                    ...updatedBatch,
                    percentKeyed: field === "percentKeyed" ? "100" : "0",
                    percentMailOrder: field === "percentMailOrder" ? "100" : "0",
                    percentInternet: field === "percentInternet" ? "100" : "0",
                    percentSwiped: field === "percentSwiped" ? "100" : "0",
                };
            }
    
            return updatedBatch;
            // Manually trigger validation after state update
    setTimeout(() => handleBlur(), 0);
        });
        
        // Real-time validation for REVIO monthly volume
        if (field === 'monthlyVolume' && revioExists) {
            const monthlyVolumeNum = Number(newValue.replace(/[^0-9.]/g, '') || 0);
            if (monthlyVolumeNum > 50000) {
                setRevioVolumeError('Please reach out to your Rev.io sales representative for further assistance. If you don\'t currently have one, feel free to contact us at sales@rev.io');
            } else {
                setRevioVolumeError('');
            }
        }
    };
    
    

    const handleBlur = () => {   
             const total =
            Number(creditBatch.percentSwiped || 0, 10) +
            Number(creditBatch.percentKeyed || 0, 10) +
            Number(creditBatch.percentInternet || 0, 10) +
            Number(creditBatch.percentMailOrder || 0, 10);
        if (total > 100) {
            setError('Total percentage cannot exceed 100.');
        } else {
            setError('');
        }
    };

    const [error1, setError1] = useState('');

    const achSecFields = [
        "achpercentNachaCCD", "achpercentNachaPOP", "achpercentNachaWEB",
        "achpercentNachaPPD", "achpercentNachaARC", "achpercentNachaBOC", "achpercentNachaTEL"
    ];

    const handleInputChange1 = (e, field) => {
        const { value } = e.target;
    
        setAchBatch((prev) => {
            let updatedBatch = { ...prev, [field]: value };
    
            if (!isNaN(value) && parseInt(value, 10) === 100 && achSecFields.includes(field)) {
                // Only reset other SEC percentage fields when a SEC field itself is set to 100
                updatedBatch = {
                    ...updatedBatch,
                    achpercentNachaCCD: field === "achpercentNachaCCD" ? "100" : "0",
                    achpercentNachaPOP: field === "achpercentNachaPOP" ? "100" : "0",
                    achpercentNachaWEB: field === "achpercentNachaWEB" ? "100" : "0",
                    achpercentNachaPPD: field === "achpercentNachaPPD" ? "100" : "0",
                    achpercentNachaARC: field === "achpercentNachaARC" ? "100" : "0",
                    achpercentNachaBOC: field === "achpercentNachaBOC" ? "100" : "0",
                    achpercentNachaTEL: field === "achpercentNachaTEL" ? "100" : "0",
                };
            }
            return updatedBatch;
            // Manually trigger validation after state update
    setTimeout(() => handleBlur1(), 0);
        });
    };
    

    const handleBlur1= () => {
        const total1 =
        Number(achBatch.achpercentKeyed || 0, 10) +
        Number(achBatch.achpercentMailOrder || 0, 10) +
        Number(achBatch.achpercentInternet || 0, 10) +
        Number(achBatch.achpercentSwiped || 0, 10) +
        Number(achBatch.achpercentNachaARC || 0, 10) +
        Number(achBatch.achpercentNachaBOC || 0, 10) +
        Number(achBatch.achpercentTelephoneOrder || 0, 10);

        if (total1 > 100) {
            setError1('Total percentage cannot exceed 100.');
        } else {
            setError1('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setRevioVolumeError('');
        if (!leadGuid) {
            setErrorMessage('Session expired or lead data is missing. Please start over from the login page.');
            return;
        }
        
        // Validate if at least one batch type is selected
        if (!selectedSections.credit && !selectedSections.ach) {
            setErrorMessage('Please select at least one Batch Type.');
            return;
        }
        const missingFields = [];
        // Helper function to validate fields
        const validateFields = (fields, batchData, batchType) => {
            fields.forEach(({ key, label }) => {
                if (!batchData[key]) {
                    missingFields.push(`${batchType}: ${label}`);
                }
            });
        };
    
        // Validate Credit Batch if selected
        if(!ediningExists){
        if (selectedSections.credit) {
            // REVIO agent validation: Check if monthly volume exceeds $50k
            if (revioExists) {
                const monthlyVolumeNum = Number(creditBatch.monthlyVolume?.replace(/[^0-9.]/g, '') || 0);
                if (monthlyVolumeNum > 50000) {
                    setErrorMessage('Please reach out to your Rev.io sales representative for further assistance. If you don\'t currently have one, feel free to contact us at sales@rev.io');
                    setRevioVolumeError('Please reach out to your Rev.io sales representative for further assistance. If you don\'t currently have one, feel free to contact us at sales@rev.io');
                    return;
                }
            }
            
            const creditFields = [
                { key: 'monthlyVolume', label: 'Monthly Volume' },
                { key: 'dailyVolume', label: 'Daily Volume' },
                { key: 'monthlyCount', label: 'Monthly Count' },
                { key: 'averageTicket', label: averageTicketLabel },
                { key: 'maximumTicket', label: 'Maximum Ticket' },
                { key: 'percentSwiped', label: 'Percent Swiped' },
                { key: 'percentKeyed', label: 'Percent Keyed' },
                { key: 'percentInternet', label: 'Percent Internet' },
                { key: 'percentMailOrder', label: 'Percent Mail Order' },
            ];
    
            validateFields(creditFields, creditBatch, 'Credit');
    
            const totalPercentage = 
                (Number(creditBatch.percentSwiped) || 0) +
                (Number(creditBatch.percentKeyed) || 0) +
                (Number(creditBatch.percentInternet) || 0) +
                (Number(creditBatch.percentMailOrder) || 0);
    
            if (totalPercentage !== 100) {
                missingFields.push('Credit: Total Percentage must equal 100 (Percent Swiped, Keyed, Internet, and Mail Order)');
            }
        }
    }
    
        // Validate ACH Batch if selected
        if (selectedSections.ach) {
            if (revioExists && !mwRevioExists && !mwGeneralExists) {
                // For base REVIO agents, ensure PPD is set to 100 before validation
                if (!achBatch.achpercentNachaPPD || String(achBatch.achpercentNachaPPD).trim() === '' || Number(achBatch.achpercentNachaPPD) !== 100) {
                    setAchBatch(prev => ({
                        ...prev,
                        achpercentNachaPPD: '100'
                    }));
                }
                
                // For base REVIO agents, only validate required fields (PPD is always 100, skip percentage validation)
                const achFields = [
                    { key: 'achmonthlyVolumeVal', label: 'Monthly Volume' },
                    { key: 'achdailyVolume', label: 'Daily Volume' },
                    { key: 'achmonthlyCount', label: 'Monthly Count' },
                    { key: 'achavgTicket', label: 'Average Ticket' },
                    { key: 'achmaximumTicket', label: 'Maximum Ticket' },
                ];
        
                validateFields(achFields, achBatch, 'ACH');
        
            } else {
                // For non-REVIO agents, validate all fields
                const achFields = [
                    { key: 'achmonthlyVolumeVal', label: 'Monthly Volume' },
                    { key: 'achdailyVolume', label: 'Daily Volume' },
                    { key: 'achmonthlyCount', label: 'Monthly Count' },
                    { key: 'achavgTicket', label: 'Average Ticket' },
                    { key: 'achmaximumTicket', label: 'Maximum Ticket' },
                    { key: 'achpercentNachaPOP', label: 'Percent Nacha POP' },
                    { key: 'achpercentNachaCCD', label: 'Percent Nacha CCD' },
                    { key: 'achpercentNachaWEB', label: 'Percent Nacha WEB' },
                    { key: 'achpercentNachaARC', label: 'Percent Nacha ARC' },
                    { key: 'achpercentNachaBOC', label: 'Percent Nacha BOC' },
                    { key: 'achpercentNachaTEL', label: 'Percent Nacha TEL' },
                    { key: 'achpercentNachaPPD', label: 'Percent Nacha PPD' },
                ];
        
                validateFields(achFields, achBatch, 'ACH');
        
                const totalAchPercentage = 
                    (Number(achBatch.achpercentNachaWEB) || 0) +
                    (Number(achBatch.achpercentNachaCCD) || 0) +
                    (Number(achBatch.achpercentNachaPOP) || 0) +
                    (Number(achBatch.achpercentNachaPPD) || 0) +
                    (Number(achBatch.achpercentNachaARC) || 0) +
                    (Number(achBatch.achpercentNachaBOC) || 0) +
                    (Number(achBatch.achpercentNachaTEL) || 0);
        
                if (totalAchPercentage !== 100) {
                    missingFields.push('ACH: Total Percentage must equal 100 (CCD, TEL, WEB, PPD, ARC, BOC and POP)');
                }
            }
        }
    
        // Display errors if any
        if (missingFields.length > 0 ) {
            setError(missingFields.join(', '));
            return;
        }
    
        try {
            const responses = [];
            setLoading(true);  //  Start loading
            // Submit Credit Batch if selected
            if (selectedSections.credit) {
                const creditRequest = {
                    leadguidVal: leadGuid,
                    agentId: agentIdVal,
                    ContactGuidVal:ContactGuid,
                    Detail: {
                        TranType: "TranType",
                        TranAction: "TranAction",
                        Ip: "Ip",
                    },
                    Batch: {
                        batchType: "Credit",
                        priorAcceptance: creditBatch.priorAcceptance,
                        monthlyVolume: ediningExists ? "50000" : creditBatch.monthlyVolume,
                        monthlyCountRefunds: creditBatch.monthlyCountRefunds,
                        monthlyVolumeRefunds: creditBatch.monthlyVolumeRefunds,
                        dailyVolume: ediningExists ? "1000" : creditBatch.dailyVolume,
                        monthlyCount:  creditBatch.monthlyCount,
                        averageTicket: ediningExists ? "250" : creditBatch.averageTicket,
                        maximumTicket: ediningExists ? "1000" : creditBatch.maximumTicket,
                        percentInternet: ediningExists ? "100" : creditBatch.percentInternet,
                        percentSwiped: ediningExists ? "0" : creditBatch.percentSwiped,
                        percentKeyed: ediningExists ? "0" : creditBatch.percentKeyed,
                        percentMailOrder: ediningExists ? "0" : creditBatch.percentMailOrder,
                                        priorTermination: creditBatch.priorTermination,
                        Accounts: {
                            Account: ((revioExists || mwRevioExists || mwGeneralExists || ensoraExists)
                                ? ['Visa', 'MasterCard', 'Amex', 'Discover']
                                : Object.keys(creditBatch.cardTypes).filter(cardType => creditBatch.cardTypes[cardType])
                            ).map(cardType => ({
                                CardType: cardType,
                                AuthHost: "AuthEmulator",
                                SettleHost: "Local",
                            })),
                        },
                    },
                };
                
                const creditResponse = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, creditRequest);
                responses.push(creditResponse);
            }
    
            // Submit ACH Batch if selected
            if (selectedSections.ach) {
                const achRequest = {
                    leadguidVal: leadGuid,
                    agentId: agentIdVal,
                    ContactGuidVal:ContactGuid,
                    Detail: {
                        TranType: "TranType",
                        TranAction: "TranAction",
                        Ip: "Ip",
                    },
                    Batch: {
                        batchType: "Ach",
                        monthlyVolume: achBatch.achmonthlyVolumeVal,
                        monthlyCount: achBatch.achmonthlyCount,
                        priorAcceptance: achBatch.achpriorAcceptance,
                        priorTermination: achBatch.achpriorTermination,
                        averageTicket: achBatch.achavgTicket,
                        monthlyCountRefunds: achBatch.achmonthlyCountRefunds,
                        monthlyVolumeRefunds: achBatch.achmonthlyVolumeRefunds,
                        maximumTicket: achBatch.achmaximumTicket,
                        dailyVolume: achBatch.achdailyVolume,
                        PercentNachaPOP: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaPOP,
                        PercentNachaCCD: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaCCD,
                        PercentNachaWEB: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaWEB,
                        PercentNachaTEL: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaTEL,
                        PercentNachaARC: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaARC,
                        PercentNachaBOC: (revioExists && !mwRevioExists && !mwGeneralExists) ? '0'   : achBatch.achpercentNachaBOC,
                        PercentNachaPPD: (revioExists && !mwRevioExists && !mwGeneralExists) ? '100' : achBatch.achpercentNachaPPD,
                    },
                }; 
                const achResponse = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, achRequest);
                responses.push(achResponse);
            }
    
            // Process responses — only navigate if ALL calls succeeded
            const failedResponses = responses.filter(
                response => response.data.response.result.responseCode !== '00'
            );

            if (failedResponses.length > 0) {
                const errorMessages = failedResponses
                    .map(r => r.data.response.result?.debugInfo || 'Unknown error')
                    .join(', ');
                setErrorMessage(errorMessages);
            } else {
                markStepComplete('volumeInfo');
                navigate('/BankInfo');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const serverMsg = error.response?.data?.message || error.response?.data || error.message;
            setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'An error occurred during submission.');
        }finally {
            setLoading(false);  // Stop loading after response
        }
    };
    

    const [error, setError] = useState('');

    // Updated handleBatchSelection to toggle the batch selection
    const handleBatchSelection = (e) => {
        const selectedBatch = e.target.value;
        setSelectedSections((prevSections) => ({
            ...prevSections,
            [selectedBatch]: true,
        }));
        setErrorMessage('');
    };

    // Credit-only agents (eDining, Ensora Flat, PromoTix) never have a second batch type to
    // offer, so there is no choice to make — open Credit automatically instead of presenting a
    // one-option dropdown.
    useEffect(() => {
        if (creditOnly && !selectedSections.credit) {
            setSelectedSections((prevSections) => ({ ...prevSections, credit: true }));
        }
    }, [creditOnly]);

    // Hide the dropdown once there is nothing left to pick: either the agent only ever offers
    // Credit, or the merchant has already added every batch type available to them.
    const hideDropdown = creditOnly || (selectedSections.credit && selectedSections.ach);

    return (
        <div className={`signature-container}`}>
          {loading && (
                <div className="loading-overlay">
                <div className="spinner-border" role="status">
                </div>
                </div>
        )}
            <Layout pageTitle="Volume Information">

                <form onSubmit={handleSubmit}>
                    {!ediningExists && !creditOnly && (
                    <div className="alert alert-info mb-4" role="alert">
                        If you would like to process ACH, please add an ACH batch below.
                    </div>
                    )}
                    {/* Dropdown to select Batch Type - hidden when there is nothing left to pick */}
                    {!hideDropdown && (
    <div className="form-group">
        <label className='label_Control' htmlFor="batchType">Select Batch Type</label>
        <select
            className="form-controling"
            id="batchType"
            onChange={handleBatchSelection}
            value=""
        >
            <option value="" disabled>Select a batch</option>
            {!selectedSections.credit && <option value="credit">Credit</option>}
            {!selectedSections.ach && !creditOnly && <option value="ach">ACH</option>}
        </select>
    </div>
)}

    
                    {/* Conditionally render Credit Batch form */}
                    {selectedSections.credit && (
                        <>
                        
    <div className="batch-header d-flex align-items-center justify-content-between">
        <h3 className='title_Control'>Credit Batch</h3>
        {/* Credit-only agents auto-open this section and have no batch-type dropdown, so
            removing it would leave them with no way to add it back. */}
        {!creditOnly && (
        <button
            type="button"
            className="btn btn-danger mt-2 ms-3"
            onClick={() => removeBatchSection('credit')}
        >
            Remove
        </button>
        )}
    </div>

                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="monthlyVolume">Monthly Volume: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="monthlyVolume"
                                        name="monthlyVolume"
                                        value={ediningExists ? "50000" : creditBatch.monthlyVolume}   
                                        onChange={(e) => handleInputChange(e, 'monthlyVolume')}
                                        required
                                    />
                                    {revioVolumeError && (
                                        <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                                            {revioVolumeError}
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="dailyVolume">Daily Volume: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="dailyVolume"
                                        name="dailyVolume"
                                        value={ediningExists ? "1000" : creditBatch.dailyVolume}
                                        onChange={(e) => handleInputChange(e, 'dailyVolume')}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="monthlyCount">Monthly Count: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="monthlyCount"
                                        name="monthlyCount"
                                        value= {creditBatch.monthlyCount}
                                        onChange={(e) => handleInputChange(e, 'monthlyCount')}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="monthlyCountRefunds">Monthly Count Refunded (Optional):</label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="monthlyCountRefunds"
                                        name="monthlyCountRefunds"
                                        value={creditBatch.monthlyCountRefunds}
                                        onChange={(e) => handleInputChange(e, 'monthlyCountRefunds')}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="monthlyVolumeRefunds">Monthly Volume Refunded (Optional):</label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="monthlyVolumeRefunds"
                                        name="monthlyVolumeRefunds"
                                        value={creditBatch.monthlyVolumeRefunds}
                                        onChange={(e) => handleInputChange(e, 'monthlyVolumeRefunds')}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="averageTicket">{averageTicketLabel}: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="averageTicket"
                                        name="averageTicket"
                                        value={ediningExists ? "250" : creditBatch.averageTicket}
                                        onChange={(e) => handleInputChange(e, 'averageTicket')}
                                        required
                                    />
                                    {promotixExists && (
                                        <small className="text-muted d-block">
                                            The average total value of a single order, not the price of one ticket.
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="maximumTicket">Max Ticket: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="maximumTicket"
                                        name="maximumTicket"
                                        value={ediningExists ? "1000" : creditBatch.maximumTicket}
                                        onChange={(e) => handleInputChange(e, 'maximumTicket')}
                                        required
                                    />
                                </div>
                            </div>

{/* Conditional rendering based on valueAgentId */}
{ediningExists ? (
    // Hidden fields for agentId eDining
    <div className="row" style={{ display: 'none' }}>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentInternet">Percent Internet:</label>
            <input
                type="number"
                className="form-controling"
                id="percentInternet"
                name="percentInternet"
                value={100} 
                readOnly
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentSwiped">Percent Swiped:</label>
            <input
                type="number"
                className="form-controling"
                id="percentSwiped"
                name="percentSwiped"
                value={0} 
                readOnly
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentKeyed">Percent Keyed:</label>
            <input
                type="number"
                className="form-controling"
                id="percentKeyed"
                name="percentKeyed"
                value={0} 
                readOnly
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentMailOrder">Percent Mail Order:</label>
            <input
                type="number"
                className="form-controling"
                id="percentMailOrder"
                name="percentMailOrder"
                value={0} 
                readOnly
            />
        </div>
    </div>
) : (
    <div className="row">
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentInternet">Percent Internet: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
            <input
                type="number"
                className="form-controling"
                id="percentInternet"
                name="percentInternet"
                value={creditBatch.percentInternet}
                onChange={(e) => handleInputChange(e, 'percentInternet')}
                onBlur={handleBlur}
                required
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentSwiped">Percent Swiped: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
            <input
                type="number"
                className="form-controling"
                id="percentSwiped"
                name="percentSwiped"
                value={creditBatch.percentSwiped}
                onChange={(e) => handleInputChange(e, 'percentSwiped')}
                onBlur={handleBlur}
                required
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentKeyed">Percent Keyed: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
            <input
                type="number"
                className="form-controling"
                id="percentKeyed"
                name="percentKeyed"
                value={creditBatch.percentKeyed}
                onChange={(e) => handleInputChange(e, 'percentKeyed')}
                onBlur={handleBlur}
                required
            />
        </div>
        <div className="col-md-4">
            <label className='label_Control' htmlFor="percentMailOrder">Percent Mail Order: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
            <input
                type="number"
                className="form-controling"
                id="percentMailOrder"
                name="percentMailOrder"
                value={creditBatch.percentMailOrder}
                onChange={(e) => handleInputChange(e, 'percentMailOrder')}
                onBlur={handleBlur}
                required
            />
        </div>
    </div>
)}



{/* Additional fields */}
<div className="row">
    <div className="col-md-4">
        <label className='label_Control' htmlFor="achpriorAcceptance">
            Prior Acceptance:
            <span className="required">*</span>
        </label>
        <div id="achpriorAcceptance" className="radio-group">
            <label className='label_Control'>
                <input
                    type="radio"
                    name="priorAcceptance"
                    value="0"
                    checked={creditBatch.priorAcceptance === "0"}
                    onChange={(e) => handleInputChange(e, "priorAcceptance")}
                    required
                />
                No
            </label>
            <label className='label_Control'>
                <input
                    type="radio"
                    name="priorAcceptance"
                    value="1"
                    checked={creditBatch.priorAcceptance === "1"}
                    onChange={(e) => handleInputChange(e, "priorAcceptance")}
                />
                Yes
            </label>
        </div>
    </div>

    <div className="col-md-4">
        <label className='label_Control' htmlFor="achpriorTermination">
            Prior Termination:
            <span className="required">*</span>
        </label>
        <div id="achpriorTermination" className="radio-group">
            <label className='label_Control'>
                <input
                    type="radio"
                    name="priorTermination"
                    value="0"
                    checked={creditBatch.priorTermination === "0"}
                    onChange={(e) => handleInputChange(e, "priorTermination")}
                />
                No
            </label>
            <label className='label_Control'>
                <input
                    type="radio"
                    name="priorTermination"
                    value="1"
                    checked={creditBatch.priorTermination === "1"}
                    onChange={(e) => handleInputChange(e, "priorTermination")}
                />
                Yes
            </label>
        </div>
    </div>
</div>

    
{!revioExists && !mwRevioExists && !mwGeneralExists && !ensoraExists && !promotixExists && !intelecomExists && (
<div className="card-selection">
  <h4 className='title_Control'>Select Card Types:</h4>
  {['Visa', 'MasterCard', 'Amex', 'Discover'].map((cardType) => (
    <label key={cardType} className="checkbox-label">
      <input
        type="checkbox"
        className='checkbox_Control'
        name={cardType}
        checked={!!creditBatch.cardTypes?.[cardType]}
        onChange={(e) => handleCardTypeChange(e, cardType)} 
      />
      {cardType}
    </label>
  ))}
</div>
)}

                        </>
                    )}
    
                    {/* Conditionally render ACH Batch form */}
                    {!ediningExists && selectedSections.ach && (
                        <>
                           <div className="batch-header d-flex align-items-center justify-content-between">
  <h3 className='title_Control'>ACH Batch</h3>
  <button 
    type="button" 
    className="btn btn-danger mt-2 ms-3" 
    onClick={() => removeBatchSection('ach')}
  >
    Remove
  </button>
</div>
                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achmonthlyVolumeVal">Monthly Volume: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achmonthlyVolumeVal"
                                        name="achmonthlyVolumeVal"
                                        value={achBatch.achmonthlyVolumeVal}
                                        onChange={(e) => handleInputChange1(e, 'achmonthlyVolumeVal')}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achDailyVolume">Daily Volume: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achDailyVolume"
                                        name="achdailyVolume"
                                        value={achBatch.achdailyVolume}
                                        onChange={(e) => handleInputChange1(e, 'achdailyVolume')}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achmonthlyCount">Monthly Count: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achmonthlyCount"
                                        name="achmonthlyCount"
                                        value={achBatch.achmonthlyCount}
                                        onChange={(e) => handleInputChange1(e, 'achmonthlyCount')}
                                        required
                                    />
                                </div>
                            </div>
    
                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achmonthlyCountRefunds">Monthly Count Refunded (Optional):</label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achmonthlyCountRefunds"
                                        name="achmonthlyCountRefunds"
                                        value={achBatch.achmonthlyCountRefunds}
                                        onChange={(e) => handleInputChange1(e, 'achmonthlyCountRefunds')}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achmonthlyVolumeRefunds">Monthly Volume Refunded (Optional):</label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achmonthlyVolumeRefunds"
                                        name="achmonthlyVolumeRefunds"
                                        value={achBatch.achmonthlyVolumeRefunds}
                                        onChange={(e) => handleInputChange1(e, 'achmonthlyVolumeRefunds')}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achAvgTicket">Average Ticket: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achAvgTicket"
                                        name="achavgTicket"
                                        value={achBatch.achavgTicket}
                                        onChange={(e) => handleInputChange1(e, 'achavgTicket')}
                                        required
                                    />
                                </div>
                            </div>
    
                            <div className="row volume-form-row">
                                <div className="col-md-4">
                                    <label className='label_Control' htmlFor="achmaximumTicket">Max Ticket: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-controling"
                                        id="achmaximumTicket"
                                        name="achmaximumTicket"
                                        value={achBatch.achmaximumTicket}
                                        onChange={(e) => handleInputChange1(e, 'achmaximumTicket')}
                                        required
                                    />
                                </div>
                            </div>
    
                            <div className="row">
                {(!revioExists || mwRevioExists || mwGeneralExists) && (
                    <div className="col-md-4">
                        <label className='label_Control' htmlFor="achpercentNachaCCD">
                            CCD: <span style={{ color: 'red', display: 'contents' }}>*</span>
                            <span title="Corporate Credit or Debit (CCD) transactions are used for business payments." style={{ marginLeft: '5px', cursor: 'help' }}> (?)
                            </span>
                        </label>
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaCCD"
                            name="achpercentNachaCCD"
                            value={achBatch.achpercentNachaCCD}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaCCD')}
                            onBlur={handleBlur1}
                            required
                        />
                    </div>
                )}
                <div className="col-md-4">
                    <label className='label_Control' htmlFor="achpercentNachaPPD">
                        PPD: <span style={{ color: 'red', display: 'contents' }}>*</span>
                        <span 
                            title="Prearranged Payment and Deposit (PPD) is commonly used for payroll and direct deposits." 
                            style={{ marginLeft: '5px', cursor: 'help', display: 'inline-block' }}
                        > 
                            (?)
                        </span>
                    </label>
                    {(revioExists && !mwRevioExists && !mwGeneralExists) ? (
                        <div className="form-controling" style={{ padding: '8px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                            100
                        </div>
                    ) : (
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaPPD"
                            name="achpercentNachaPPD"
                            value={achBatch.achpercentNachaPPD}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaPPD')}
                            onBlur={handleBlur1}
                            required
                        />
                    )}
                </div>
                {(!revioExists || mwRevioExists || mwGeneralExists) && (
                    <div className="col-md-4">
                        <label className='label_Control' htmlFor="achpercentNachaWEB">
                            WEB: <span style={{ color: 'red', display: 'contents' }}>*</span>
                            <span title="WEB transactions are electronic payments made over the internet." style={{ marginLeft: '5px', cursor: 'help' }}> (?)
                            </span>
                        </label>
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaWEB"
                            name="achpercentNachaWEB"
                            value={achBatch.achpercentNachaWEB}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaWEB')}
                            onBlur={handleBlur1}
                            required
                        />
                    </div>
                )}
            </div>
            {!revioExists && !mwRevioExists && !mwGeneralExists && !ensoraExists && !promotixExists && !intelecomExists && (
                <div className="row">
                    <div className="col-md-4">
                        <label className='label_Control' htmlFor="achpercentNachaTEL">
                            TEL: <span style={{ color: 'red', display: 'contents' }}>*</span>
                            <span title="Telephone-initiated (TEL) transactions are authorized over the phone." style={{  marginLeft: '5px', cursor: 'help' }}> (?)
                            </span>
                        </label>
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaTEL"
                            name="achpercentNachaTEL"
                            value={achBatch.achpercentNachaTEL}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaTEL')}
                            onBlur={handleBlur1}
                            required
                        />
                    </div>
                    <div className="col-md-4">
                        <label className='label_Control' htmlFor="achpercentNachaPOP">
                            POP: <span style={{ color: 'red', display: 'contents' }}>*</span>
                            <span title="Point of Purchase (POP) transactions occur when a check is converted into an ACH payment at the point of sale." style={{ marginLeft: '5px', cursor: 'help' }}> (?)
                            </span>
                        </label>
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaPOP"
                            name="achpercentNachaPOP"
                            value={achBatch.achpercentNachaPOP}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaPOP')}
                            onBlur={handleBlur1}
                            required
                        />
                    </div>
                    <div className="col-md-4">
                        <label className='label_Control' htmlFor="achpercentNachaARC">
                            ARC: <span style={{ color: 'red', display: 'contents' }}>*</span>
                            <span title="Accounts Receivable Entry indicating a single debit transaction used to convert a check received via mail, dropbox, or delivered in person for payment." style={{  marginLeft: '5px', cursor: 'help' }}> (?)
                            </span>
                        </label>
                        <input
                            type="number"
                            className="form-controling"
                            id="achpercentNachaARC"
                            name="achpercentNachaARC"
                            value={achBatch.achpercentNachaARC}
                            onChange={(e) => handleInputChange1(e, 'achpercentNachaARC')}
                            onBlur={handleBlur1}
                            required
                        />
                    </div>
                </div>
            )}
            {!revioExists && !mwRevioExists && !mwGeneralExists && !ensoraExists && !promotixExists && !intelecomExists && (
                <div className="col-md-4">
                    <label className='label_Control' htmlFor="achpercentNachaBOC">
                        BOC: <span style={{ color: 'red', display: 'contents' }}>*</span>
                        <span title="Back Office Conversion Entry identifying transactions where a paper check payment received at a non-financial institution location is converted into a single-entry ACH debit during back-office processing" style={{  marginLeft: '5px', cursor: 'help' }}> (?)
                        </span>
                    </label>
                    <input
                        type="number"
                        className="form-controling"
                        id="achpercentNachaBOC"
                        name="achpercentNachaBOC"
                        value={achBatch.achpercentNachaBOC}
                        onChange={(e) => handleInputChange1(e, 'achpercentNachaBOC')}
                        onBlur={handleBlur1}
                        required
                    />
                </div>
            )}
           
<div className="row">
    <div className="col-md-4">
        <label className='label_Control' htmlFor="achpriorAcceptance">Prior Acceptance: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
        <div id="achpriorAcceptance" className="radio-group">
            <label className='label_Control'>
                <input
                    type="radio"
                    name="achpriorAcceptance"
                    value="0"
                    checked={achBatch.achpriorAcceptance === "0"}
                    onChange={(e) => handleInputChange1(e, "achpriorAcceptance")}
                />
                No
            </label>
            <label className='label_Control'>
                <input
                    type="radio"
                    name="achpriorAcceptance"
                    value="1"
                    checked={achBatch.achpriorAcceptance === "1"}
                    onChange={(e) => handleInputChange1(e, "achpriorAcceptance")}
                />
                Yes
            </label>
        </div>
    </div>

    <div className="col-md-4">
        <label className='label_Control' htmlFor="achpriorTermination">Prior Termination: <span style={{ color: 'red', display: 'contents' }}>*</span></label>
        <div id="achpriorTermination" className="radio-group">
            <label className='label_Control'>
                <input
                    type="radio"
                    name="achpriorTermination"
                    value="0"
                    checked={achBatch.achpriorTermination === "0"}
                    onChange={(e) => handleInputChange1(e, "achpriorTermination")}
                />
                No
            </label>
            <label className='label_Control'>
                <input
                    type="radio"
                    name="achpriorTermination"
                    value="1"
                    checked={achBatch.achpriorTermination === "1"}
                    onChange={(e) => handleInputChange1(e, "achpriorTermination")}
                />
                Yes
            </label>
        </div>
    </div>
</div>

                            
                        </>
                    )}
    
    <div className="d-flex justify-content-center mt-3">
    <button type="submit" className="btn btn-success px-5" style={{ width: '370px' }}>
        Next
    </button>
        </div>
                    
                {error && (
                <div className="row">
                    <div className="col-md-12">
                        <small style={{ color: 'red' }}>{error}</small>
                    </div>
                </div>
            )}
                {errorMessage && (
                <div className="row">
                    <div className="col-md-12">
                        <small style={{ color: 'red' }}>{errorMessage}</small>
                    </div>
                </div>
            )}
                {successMessage && (
                <div className="row">
                    <div className="col-md-12">
                        <div className="alert alert-success mt-2">{successMessage}</div>
                    </div>
                </div>
            )}
                    <p style={{ color: 'red' }}>* Required field</p>
                </form>
            </Layout>
        </div>
    );
};

export default Volume;
